/* eslint-disable import/no-named-as-default-member */
import React, { useEffect, RefObject } from "react";
import Hls, { HlsConfig } from "hls.js";

export interface HlsPlayerProps
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  hlsConfig?: Partial<HlsConfig>;
  playerRef?: RefObject<HTMLVideoElement>;
  src: string;
  isHls: boolean;
}

export const ReactHlsPlayer = (props: HlsPlayerProps) => {
  const {
    hlsConfig,
    playerRef = React.createRef<HTMLVideoElement>(),
    src,
    autoPlay,
    isHls,
    ...restProps
  } = props;

  useEffect(() => {
    let hls: Hls;

    const initPlayer = () => {
      if (hls != null) {
        hls.destroy();
      }

      const newHls = new Hls({
        enableWorker: false,
        ...hlsConfig,
      });

      if (playerRef.current != null) {
        newHls.attachMedia(playerRef.current);
      }

      newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
        newHls.loadSource(src);

        newHls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (autoPlay) {
            playerRef?.current
              ?.play()
              .catch(() =>
                console.warn("Unable to autoplay")
              );
          }
        });
      });

      newHls.on(Hls.Events.ERROR, function (event, data) {
        if (data.fatal) {
          switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            newHls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            newHls.recoverMediaError();
            break;
          default:
            initPlayer();
            break;
          }
        }
      });

      hls = newHls;
    };

    // Media Source support
    if (Hls.isSupported() && isHls) {
      initPlayer();
    }

    return () => {
      if (hls != null) {
        hls.destroy();
      }
    };
  }, [autoPlay, hlsConfig, playerRef, src, isHls]);

  if (Hls.isSupported() && isHls) {
    return <video ref={playerRef} {...restProps} />;
  }

  return (
    <video ref={playerRef} autoPlay={autoPlay} {...restProps}>
      { isHls
        ? <source type="application/vnd.apple.mpegurl" src={src} />
        : <source type="video/mp4" src={src} />
      }
    </video>
  );
};
